import axios from 'axios'
import type { ApiResponse, PaginatedResponse } from '@panda/shared-types'
import { useAuthStore } from '@/store/auth'

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    // CSRF 防护：自定义请求头，跨站 form 提交无法设置，后端据此区分合法 XHR 与 CSRF 攻击
    'X-Requested-With': 'XMLHttpRequest',
  },
})

/** zustand persist 的 storage key，与 auth.ts 中 persist({ name: 'auth-storage' }) 保持一致。 */
const PERSIST_KEY = 'auth-storage'

/**
 * 读取 zustand persist 原始持久化状态（未 rehydrate 到内存前的 raw JSON）。
 * auth.ts store 使用 partialize 写入 `{ user, token, isAuthenticated }` 三个字段，
 * 封装为一个 helper 避免多个调用方重复 JSON.parse 与字段路径判断。
 */
function readPersistState(): { token: string | null; isAuthenticated: boolean } {
  try {
    const raw = localStorage.getItem(PERSIST_KEY)
    if (!raw) return { token: null, isAuthenticated: false }
    const parsed = JSON.parse(raw)
    // zustand persist 默认序列化格式：{ state: { token, isAuthenticated, user }, version: 0 }
    // 兼容旧版本可能直接平铺的情况
    const state = parsed?.state ?? parsed ?? {}
    const token = typeof state.token === 'string' && state.token.length > 0 ? state.token : null
    const isAuthenticated = Boolean(token || state.isAuthenticated)
    return { token, isAuthenticated }
  } catch {
    return { token: null, isAuthenticated: false }
  }
}

/**
 * 从 zustand persist 的 localStorage 原始 JSON 中读取 token。
 * 只在 zustand store 内存中 token 为空时调用，用作竞态兜底。
 * 直接读 raw JSON 避免与 persist 内部的 rehydration 调度竞争（persist 用微任务异步 setState，
 * 登录后 setToken → 微任务 rehydrate 可能用 null 覆盖内存值，导致请求拦截器读不到内存 token）。
 * 解析失败或字段缺失返回 null，上层继续走 Cookie 通道。
 */
function readTokenFromPersistStorage(): string | null {
  return readPersistState().token
}

/**
 * 检查本地是否存在已登录的证据（zustand 内存态、或 persist localStorage 中的 token/isAuthenticated 标记）。
 * 用于 __root.tsx verify() 守卫：完全冷启动（无内存态 + 无 persist 残留）时
 * 不需要调 /auth/verify，否则无意义 401 → 触发 401 拦截器跳登录。
 */
export function hasLocalAuthEvidence(): boolean {
  // 先看 zustand 内存（rehydrate 已完成时这里是权威）
  const memState = useAuthStore.getState()
  if (memState.isAuthenticated || memState.token) return true
  // 内存没迹象，再看 localStorage persist（rehydrate 是异步微任务，
  // F5 刚刷新时可能 localStorage 已有值但内存还没 setState 到）
  const persisted = readPersistState()
  return persisted.isAuthenticated || !!persisted.token
}

/**
 * 请求拦截器：注入 Authorization Bearer token。
 * 移动端浏览器（iOS Safari ITP / Android Chrome）会拦截跨站第三方 Cookie，
 * 即使后端 Set-Cookie 也保存不住。这里从 zustand store（或兜底 localStorage persist 原始值）
 * 读取 token 加到 header，让后端 authMiddleware 通过 Authorization 识别身份
 * （后端已支持 cookie+bearer 双通道，优先 Authorization）。
 */
api.interceptors.request.use((config) => {
  // 优先读 zustand 内存中的 token（已 rehydrate 完成则是权威值）
  let token = useAuthStore.getState().token
  // 若内存中为空但 localStorage 里已经有（persist rehydrate 微任务还没跑完的竞态），
  // 直接从 localStorage raw JSON 兜底捞，避免登录后首批请求漏带 Bearer 导致 401 闪回
  if (!token) {
    token = readTokenFromPersistStorage()
  }
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

/**
 * 全局 401 跳转守卫：并发请求同时 401 时只执行一次 logout + 跳登录，
 * 避免多次 window.location.href 互相覆盖导致"一堆报错一闪而过"的视觉抖动。
 * 跳转完成（新页面加载）后变量重置，不影响下次会话。
 */
let _authRedirecting = false

function handle401Once() {
  if (_authRedirecting) return
  _authRedirecting = true
  if (import.meta.env.DEV) console.debug('[Auth] 401，清理登录态并跳转登录页（仅执行一次）')
  try {
    useAuthStore.getState().logout()
  } catch {
    /* logout 内部会清 localStorage，失败也要继续跳登录 */
  }
  // 延时一小段再跳，让当前事件循环中已 resolve/reject 的 Promise 链先跑完，
  // 避免后续请求又触发 error 拦截器再次走 401 分支
  setTimeout(() => {
    window.location.href = '/login'
  }, 0)
}

api.interceptors.response.use(
  (response) => {
    if (response.config.responseType === 'blob') {
      return response
    }
    return response.data
  },
  (error) => {
    if (error.response?.status === 401) {
      handle401Once()
    }
    return Promise.reject(error.response?.data || error.message)
  }
)

export const authApi = {
  login: (data: { email: string; code: string }) =>
    api.post('/auth/login', data) as Promise<ApiResponse<{ token: string; user: any }>>,
  sendCode: (email: string) =>
    api.post('/auth/send-code', { email }) as Promise<ApiResponse<void> & { debugCode?: string }>,
  verify: () =>
    api.get('/auth/verify') as Promise<ApiResponse<{ user: any }>>,
  logout: () =>
    api.post('/auth/logout') as Promise<ApiResponse<void>>,
  status: () =>
    api.get('/auth/status') as Promise<ApiResponse<{ initialized: boolean }>>,
}

export const usersApi = {
  getList: () =>
    api.get('/users') as Promise<ApiResponse<any[]>>,
  
  getById: (id: string) =>
    api.get(`/users/${id}`) as Promise<ApiResponse<any>>,
  
  create: (data: any) =>
    api.post('/users', data) as Promise<ApiResponse<any>>,
  
  update: (id: string, data: any) =>
    api.put(`/users/${id}`, data) as Promise<ApiResponse<void>>,
  
  delete: (id: string) =>
    api.delete(`/users/${id}`) as Promise<ApiResponse<void>>,
}

export const nutstoreApi = {
  getFolders: () =>
    api.get('/nutstore/folders') as Promise<ApiResponse<Array<{ 
      name: string
      path: string
      isSynced: boolean
      clientId: string | null
      clientNeedsUpdate: boolean
    }>>>,

  sync: () =>
    api.post('/nutstore/sync') as Promise<ApiResponse<{
      total: number
      newClients: Array<{ id: string; name: string; code: string }>
      skippedClients: string[]
      message: string
    }>>,

  importClients: (clients: Array<{
    folderName: string
    code?: string
    billingType?: 'source' | 'target'
    enableTM?: boolean
  }>) =>
    api.post('/nutstore/import-clients', { clients }) as Promise<ApiResponse<{
      newClients: Array<{ id: string; name: string; code: string }>
      skippedClients: string[]
      message: string
    }>>,

  confirmClient: (clientId: string) =>
    api.post(`/nutstore/clients/${clientId}/confirm`) as Promise<ApiResponse<void>>,
}

export const clientsApi = {
  // includeInactive=true 时包含已停用/已彻底删除客户（仅 PM/founder 生效）
  getList: (includeInactive?: boolean) =>
    api.get('/clients', { params: includeInactive ? { includeInactive: 'true' } : undefined }) as Promise<ApiResponse<any[]>>,

  getById: (id: string) =>
    api.get(`/clients/${id}`) as Promise<ApiResponse<any>>,

  create: (data: any) =>
    api.post('/clients', data) as Promise<ApiResponse<any>>,

  update: (id: string, data: any) =>
    api.put(`/clients/${id}`, data) as Promise<ApiResponse<void>>,

  // 停用客户（软删除，可恢复）
  delete: (id: string) =>
    api.delete(`/clients/${id}`) as Promise<ApiResponse<void>>,

  // 彻底删除客户：清空 TM/术语/RAG/反馈等知识资产，保留项目记录；要求全部项目已归档
  purge: (id: string) =>
    api.post(`/clients/${id}/purge`) as Promise<ApiResponse<{ deletedR2Files: number }>>,

  updateRAG: (id: string, markdown: string) =>
    api.put(`/clients/${id}/rag`, { markdown }) as Promise<ApiResponse<void>>,

  toggleTM: (id: string, enableTM: boolean) =>
    api.patch(`/clients/${id}/tm`, { enableTM }) as Promise<ApiResponse<{ enableTM: boolean }>>,

  getRAGHints: (id: string, sourceText: string) =>
    api.get(`/clients/${id}/rag-hints`, { params: { sourceText } }) as Promise<ApiResponse<{
      rag: string[]
      feedbacks: Array<{
        id: string
        fileName: string
        rawContent: string
        isRecurring: boolean
        createdAt: string
      }>
      terms: Array<{
        id: string
        source: string
        target: string
        context?: string
      }>
    }>>,
}

/**
 * 翻译记忆库（TM）API
 * 路径前缀 /clients/:clientId/tm
 */
export const tmApi = {
  /**
   * 上传 ZIP 包导入 TM（双列双语 xlsx 批量导入）
   * @param clientId 客户 ID
   * @param file ZIP 文件
   * @param onUploadProgress 上传进度回调
   */
  import: (clientId: string, file: File, onUploadProgress?: (progress: number) => void) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/clients/${clientId}/tm/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000, // 5 分钟，大 ZIP + Python 解析可能较慢
      onUploadProgress: onUploadProgress ? (e) => {
        const total = e.total || 0
        const loaded = e.loaded || 0
        onUploadProgress(total > 0 ? Math.round((loaded / total) * 100) : 0)
      } : undefined,
    }) as Promise<ApiResponse<{
      inserted: number
      duplicated: number
      skippedUnknownLang: number
      totalParsed: number
      files: Array<{
        name: string
        sourceLang: string
        targetLang: string
        count: number
        skipped: number
      }>
      skippedFiles: Array<{ name: string; reason: string }>
    }>>
  },

  /** 按语言对分组返回 TM 统计 */
  getStats: (clientId: string) =>
    api.get(`/clients/${clientId}/tm/stats`) as Promise<ApiResponse<{
      groups: Array<{ sourceLang: string; targetLang: string; count: number }>
      total: number
    }>>,

  /** 分页查询 TM 列表 */
  list: (clientId: string, params?: {
    page?: number
    pageSize?: number
    sourceLang?: string
    targetLang?: string
    search?: string
  }) =>
    api.get(`/clients/${clientId}/tm`, { params }) as Promise<ApiResponse<{
      items: Array<{
        id: string
        clientId: string
        sourceText: string
        targetText: string
        sourceLang: string
        targetLang: string
        projectId: string | null
        createdAt: string
        updatedAt: string | null
      }>
      pagination: {
        page: number
        pageSize: number
        total: number
        totalPages: number
      }
    }>>,

  /** 删除单条 TM */
  delete: (clientId: string, id: string) =>
    api.delete(`/clients/${clientId}/tm/${id}`) as Promise<ApiResponse<void>>,

  /** 清空客户所有 TM（需 confirm=true） */
  clearAll: (clientId: string) =>
    api.delete(`/clients/${clientId}/tm`, { params: { confirm: 'true' } }) as Promise<ApiResponse<void>>,
}

export const termsApi = {
  getByClientId: (clientId: string) =>
    api.get(`/terms/client/${clientId}`) as Promise<ApiResponse<any[]>>,
  
  lookup: (clientId: string, sourceLang: string, targetLang: string) =>
    api.get('/terms/lookup', { params: { clientId, sourceLang, targetLang } }) as Promise<ApiResponse<any[]>>,
  
  create: (data: any) =>
    api.post('/terms', data) as Promise<ApiResponse<any>>,
  
  update: (id: string, data: any) =>
    api.put(`/terms/${id}`, data) as Promise<ApiResponse<void>>,
  
  delete: (id: string) =>
    api.delete(`/terms/${id}`) as Promise<ApiResponse<void>>,
  
  import: (clientId: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/terms/import/${clientId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }) as Promise<ApiResponse<{ count: number }>>
  },

  bulkImport: (clientId: string, terms: any[]) =>
    api.post(`/terms/bulk`, { clientId, terms }) as Promise<ApiResponse<void>>,

  // 译者提交术语建议
  suggest: (data: {
    projectId: string
    clientId: string
    sourceText: string
    targetText: string
    context?: string
  }) => api.post('/terms/suggest', data) as Promise<ApiResponse<any>>,

  // 术语候选管理
  getCandidates: (projectId: string, status?: string) =>
    api.get(`/projects/${projectId}/term-candidates`, { params: { status } }) as Promise<ApiResponse<{ candidates: any[] }>>,

  getCandidatesByClient: (clientId: string, status?: string) =>
    api.get('/terms/candidates', { params: { clientId, status } }) as Promise<ApiResponse<{ candidates: any[] }>>,

  updateCandidate: (candidateId: string, data: any) =>
    api.put(`/terms/candidates/${candidateId}`, data) as Promise<ApiResponse<void>>,

  approveCandidate: (candidateId: string) =>
    api.post(`/terms/candidates/${candidateId}/approve`, {}) as Promise<ApiResponse<void>>,

  rejectCandidate: (candidateId: string) =>
    api.post(`/terms/candidates/${candidateId}/reject`, {}) as Promise<ApiResponse<void>>,

  batchApproveCandidates: (candidateIds: string[]) =>
    api.post('/terms/candidates/batch-approve', { candidateIds }) as Promise<ApiResponse<{ approved: number; failed: number }>>,

  batchRejectCandidates: (candidateIds: string[]) =>
    api.post('/terms/candidates/batch-reject', { candidateIds }) as Promise<ApiResponse<{ rejected: number; failed: number }>>,
}

export const feedbackApi = {
  getByProject: (projectId: string) =>
    api.get(`/feedback/project/${projectId}`) as Promise<ApiResponse<any[]>>,

  getByClient: (clientId: string) =>
    api.get(`/feedback/client/${clientId}`) as Promise<ApiResponse<any[]>>,

  create: (data: {
    clientId: string
    projectId?: string
    segmentId?: string
    fileName: string
    contentType: 'text' | 'docx' | 'xlsx' | 'image'
    rawContent?: string
  }) => api.post('/feedback', data) as Promise<ApiResponse<any>>,

  updateStatus: (id: string, data: { status?: string; isRecurring?: boolean }) =>
    api.put(`/feedback/${id}/status`, data) as Promise<ApiResponse<any>>,

  extractToRag: (id: string) =>
    api.post(`/feedback/${id}/extract-to-rag`) as Promise<ApiResponse<void>>,

  delete: (id: string) =>
    api.delete(`/feedback/${id}`) as Promise<ApiResponse<void>>,
}

export const projectsApi = {
  getList: (params?: any) =>
    api.get('/projects', { params }) as Promise<ApiResponse<PaginatedResponse<any>>>,
  
  getById: (id: string) =>
    api.get(`/projects/${id}`) as Promise<ApiResponse<any>>,

  create: (formData: FormData, onUploadProgress?: (progress: number) => void) =>
    api.post('/projects/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      onUploadProgress: onUploadProgress ? (e) => {
        const total = e.total || 0
        const loaded = e.loaded || 0
        onUploadProgress(total > 0 ? Math.round((loaded / total) * 100) : 0)
      } : undefined,
    }) as Promise<ApiResponse<any>>,

  /**
   * 分片上传 - 初始化：创建 R2 multipart upload
   * 返回 uploadId/fileKey/partSize/totalParts（命中 60 秒重复检查时返回 isDuplicate+projectId）
   */
  uploadInit: (data: {
    fileName: string
    fileSize: number
    contentType: string
    clientId: string
  }) => api.post('/projects/upload/init', data, { timeout: 30000 }) as Promise<ApiResponse<{
    uploadId: string
    fileKey: string
    partSize: number
    totalParts: number
    isDuplicate?: boolean
    projectId?: string
  }>>,

  /**
   * 分片上传 - 上传单个分片（带自动重试）
   * 单片失败会自动重试 3 次（指数退避 1s/2s/4s），避免偶发网络抖动导致整个上传中断
   * onProgress 是当前分片自身的进度（0-100），整体进度由调用方按 partNumber 累加
   */
  uploadPart: async (
    data: { uploadId: string; fileKey: string; partNumber: number; chunk: Blob },
    onProgress?: (progress: number) => void,
  ) => {
    const MAX_RETRIES = 3
    let lastError: any = null

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      // 每次重试新建 FormData（body 已被 axios 发送时消费，不能复用）
      const formData = new FormData()
      formData.append('uploadId', data.uploadId)
      formData.append('fileKey', data.fileKey)
      formData.append('partNumber', data.partNumber.toString())
      formData.append('chunk', data.chunk)

      try {
        const resp = await api.post('/projects/upload/part', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          // 单片 10MB，给 120 秒兜底（慢网络 + R2 落盘延迟）
          timeout: 120000,
          onUploadProgress: onProgress ? (e) => {
            const total = e.total || 0
            const loaded = e.loaded || 0
            onProgress(total > 0 ? Math.round((loaded / total) * 100) : 0)
          } : undefined,
        }) as ApiResponse<{ partNumber: number; etag: string }>

        if (resp.success && resp.data) {
          return resp
        }
        // API 返回失败（非 2xx），抛错进入 catch 做重试
        throw new Error(resp.error || `part ${data.partNumber} upload failed`)
      } catch (error: any) {
        lastError = error
        // 最后一次重试失败不再等待
        if (attempt < MAX_RETRIES) {
          const backoffMs = 1000 * Math.pow(2, attempt - 1) // 1s, 2s, 4s
          console.warn(`[Upload] Part ${data.partNumber} attempt ${attempt}/${MAX_RETRIES} failed, retrying in ${backoffMs}ms:`, error.message)
          await new Promise(resolve => setTimeout(resolve, backoffMs))
        }
      }
    }
    throw lastError || new Error(`Part ${data.partNumber} upload failed after ${MAX_RETRIES} retries`)
  },

  /**
   * 分片上传 - 完成：合并所有分片 + 调 Python Engine 解析 + 建项目 + 处理参考文件
   * 大文件解析可能较慢，给 5 分钟超时
   */
  uploadComplete: (data: {
    uploadId: string
    fileKey: string
    parts: { partNumber: number; etag: string }[]
    fileName: string
    clientId: string
    documentType: string
    sourceLanguage: string
    targetLanguage: string
    deadline?: string
    extractTermsBeforeTranslation: boolean
    subtitleOutputMode: string
    subtitleTopLanguage: string
    dramaTaskType: string
    referenceFiles?: File[]
  }) => {
    const formData = new FormData()
    formData.append('uploadId', data.uploadId)
    formData.append('fileKey', data.fileKey)
    formData.append('parts', JSON.stringify(data.parts))
    formData.append('fileName', data.fileName)
    formData.append('clientId', data.clientId)
    formData.append('documentType', data.documentType)
    formData.append('sourceLanguage', data.sourceLanguage)
    formData.append('targetLanguage', data.targetLanguage)
    formData.append('extractTermsBeforeTranslation', data.extractTermsBeforeTranslation.toString())
    formData.append('subtitleOutputMode', data.subtitleOutputMode)
    formData.append('subtitleTopLanguage', data.subtitleTopLanguage)
    formData.append('dramaTaskType', data.dramaTaskType)
    if (data.deadline) formData.append('deadline', data.deadline)
    if (data.referenceFiles && data.referenceFiles.length > 0) {
      data.referenceFiles.forEach((f) => formData.append('referenceFiles', f))
    }
    return api.post('/projects/upload/complete', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    }) as Promise<ApiResponse<any>>
  },

  /**
   * 分片上传 - 取消：清理 R2 中未完成的 multipart upload，避免占存储
   * 失败时不抛错（best-effort 清理）
   */
  uploadAbort: (data: { uploadId: string; fileKey: string }) =>
    api.post('/projects/upload/abort', data, { timeout: 15000 }) as Promise<ApiResponse<void>>,

  startTranslation: (id: string) =>
    // Engine 可能休眠，Workers 会先 ping /health 唤醒（最坏 2 次重试 ~92s）再调 /translate/start
    // 给 150s 覆盖冷启动+启动全流程
    api.post(`/projects/${id}/start-translation`, undefined, { timeout: 150000 }) as Promise<ApiResponse<void>>,

  stopTranslation: (id: string) =>
    api.post(`/projects/${id}/stop-translation`) as Promise<ApiResponse<void>>,

  confirmReference: (id: string) =>
    api.post(`/projects/${id}/confirm-reference`) as Promise<ApiResponse<void>>,

  // 参考文献精确匹配 PM 确认：预览命中项 + 保存取消采纳的 source 列表
  previewReferenceMatches: (id: string) =>
    api.get(`/projects/${id}/reference-matches/preview`) as Promise<ApiResponse<{
      matches: Array<{ source: string; target: string; hitCount: number }>;
      excludedSources: string[];
      autoConfirmed?: boolean;
    }>>,

  confirmReferenceMatches: (id: string, excludedSources: string[]) =>
    api.post(`/projects/${id}/reference-matches/confirm`, { excludedSources }) as Promise<ApiResponse<void>>,

  updateReferenceFile: (projectId: string, fileId: string, parsedContent: string) =>
    api.patch(`/projects/${projectId}/reference-files/${fileId}`, { parsedContent }) as Promise<ApiResponse<void>>,

  completeReview: (id: string) =>
    api.post(`/projects/${id}/complete-review`) as Promise<ApiResponse<void>>,

  completeQC: (id: string) =>
    api.post(`/projects/${id}/complete-qc`) as Promise<ApiResponse<void>>,

  reopen: (id: string) =>
    api.post(`/projects/${id}/reopen`) as Promise<ApiResponse<void>>,

  update: (id: string, data: any) =>
    api.patch(`/projects/${id}`, data) as Promise<ApiResponse<void>>,

  importRevision: (id: string, file: File) => {
    const formData = new FormData()
    formData.append('file', file)
    return api.post(`/projects/${id}/import-revision`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }) as Promise<ApiResponse<any>>
  },

  // 异步导出：POST 触发 → 轮询 status → GET download
  // 解决 CF 边缘 100s 超时（524）：Python Engine 异步生成，Workers 只做触发+轮询+R2 流式下载
  exportTrigger: (id: string, type: 'final' | 'bilingual' | 'bilingual-docx') =>
    api.post(`/projects/${id}/export`, { type }) as Promise<ApiResponse<{ taskId: string; type: string }>>,

  exportStatus: (id: string, taskId: string) =>
    api.get(`/projects/${id}/export/status`, { params: { taskId } }) as Promise<ApiResponse<{ status: string; fileKey?: string; error?: string }>>,

  // 导出文件下载：返回 presigned URL JSON，前端拿到 url 后用 <a download> 触发浏览器直连 R2 下载
  // 跳过 Workers 流式代理，速度更快且原生支持断点续传，无大文件 timeout 问题
  exportDownload: (id: string, taskId: string, type: string) =>
    api.get(`/projects/${id}/export/download`, {
      params: { taskId, type },
    }) as Promise<ApiResponse<{ url: string; filename: string; expiresIn: number }>>,

  // 源文件下载：返回 presigned URL JSON，浏览器直连 R2 下载（所有人可用）
  sourceDownload: (id: string) =>
    api.get(`/projects/${id}/source-download`) as Promise<ApiResponse<{ url: string; filename: string; expiresIn: number }>>,

  batchExportTrigger: (projectIds: string[], format: 'original' | 'bilingual' | 'both' | 'bilingual-docx') =>
    api.post('/projects/batch-export', { projectIds, format }) as Promise<ApiResponse<{
      tasks: Array<{ taskId: string; type: string; projectId: string; fileName: string }>
    }>>,

  batchExportStatus: (taskIds: string[]) =>
    api.post('/projects/batch-export/status', { taskIds }) as Promise<ApiResponse<{
      tasks: Array<{ taskId: string; status: string; fileKey?: string; error?: string }>
      allDone: boolean
      hasError: boolean
    }>>,

  delete: (id: string) =>
    api.delete(`/projects/${id}`) as Promise<ApiResponse<void>>,

  // 回收站：软删除项目列表（deleted_at 非空）
  getRecycleBin: () =>
    api.get('/projects/recycle-bin') as Promise<ApiResponse<any[]>>,

  // 回收站：恢复项目（清空 deleted_at，关联数据/R2 未删，完整还原）
  restoreFromRecycleBin: (id: string) =>
    api.post(`/projects/${id}/restore`) as Promise<ApiResponse<void>>,
}

export const segmentsApi = {
  // sinceMs：变更预检，后端先查 MAX(updated_at)，未变化返回 { unchanged: true, data: [] }
  // 前端识别 unchanged 后跳过缓存替换，避免无效的全量段落读取
  getByProject: (projectId: string, sinceMs?: number) =>
    api.get(`/segments?projectId=${projectId}${sinceMs ? `&sinceMs=${sinceMs}` : ''}`) as Promise<ApiResponse<any[]> & { maxUpdatedAt?: number; unchanged?: boolean }>,

  // 更新单条段落；若 translation 变更且本段未标记 sync_disabled，后端会同步同源文段落
  // 返回 syncedCount 表示联动覆盖的相同原文段落数
  update: (id: string, data: any) =>
    api.put(`/segments/${id}`, data) as Promise<ApiResponse<void> & { syncedCount?: number }>,

  batchUpdate: (data: any[]) =>
    api.put('/segments/batch', data) as Promise<ApiResponse<void>>,

  confirm: (id: string) =>
    api.post(`/segments/${id}/confirm`) as Promise<ApiResponse<void>>,

  // 切换段落的"不跟进重复原文同步"开关
  syncToggle: (id: string) =>
    api.post(`/segments/${id}/sync-toggle`) as Promise<ApiResponse<{ syncDisabled: boolean }>>,
}

export const housestylesApi = {
  getList: () =>
    api.get('/house-style') as Promise<ApiResponse<any[]>>,
  
  getByType: (type: string) =>
    api.get(`/house-style/${type}`) as Promise<ApiResponse<any>>,
  
  upsert: (data: { contentType: string; markdownContent: string }) =>
    api.post('/house-style', data) as Promise<ApiResponse<void>>,
  
  delete: (id: string) =>
    api.delete(`/house-style/${id}`) as Promise<ApiResponse<void>>,
}

// 风格范本库：按内容类型维护示例译文对，并从范本提炼风格指南
export const styleExamplesApi = {
  // 获取某内容类型的全部范本
  getList: (contentType: string) =>
    api.get(`/house-style/${contentType}/examples`) as Promise<ApiResponse<any[]>>,
  // 新增一条范本
  add: (contentType: string, data: { sourceText: string; targetText: string; notes?: string }) =>
    api.post(`/house-style/${contentType}/examples`, data) as Promise<ApiResponse<void>>,
  // 更新一条范本
  update: (id: string, data: { sourceText?: string; targetText?: string; notes?: string }) =>
    api.put(`/house-style/examples/${id}`, data) as Promise<ApiResponse<void>>,
  // 删除一条范本
  remove: (id: string) =>
    api.delete(`/house-style/examples/${id}`) as Promise<ApiResponse<void>>,
  // 从范本提炼风格指南（转发引擎 LLM）
  extract: (data: {
    contentType: string
    contentTypeName?: string
    examples: { sourceText: string; targetText: string; notes?: string }[]
  }) => api.post('/house-style/extract', data) as Promise<ApiResponse<{ styleGuide: string }>>,
}

// 机翻 agent 各步骤 prompt 模板外显管理 + 测试场
export const promptTemplatesApi = {
  // 获取全部场景模板（含元信息与是否已建模板标识）
  getList: () =>
    api.get('/prompt-templates') as Promise<ApiResponse<any[]>>,
  // 获取单个场景模板
  get: (scene: string) =>
    api.get(`/prompt-templates/${scene}`) as Promise<ApiResponse<any>>,
  // 保存（按 scene upsert）
  upsert: (scene: string, data: { promptContent: string; name?: string; description?: string }) =>
    api.put(`/prompt-templates/${scene}`, data) as Promise<ApiResponse<void>>,
  // 测试场：渲染模板 + 跑一次 LLM
  test: (data: {
    scene: string
    promptContent?: string
    systemPrompt?: string
    variables: Record<string, any>
    temperature?: number
  }) => api.post('/prompt-templates/test', data) as Promise<ApiResponse<{
    scene: string
    renderedPrompt: string
    systemPrompt: string | null
    output: string
    temperature: number
  }>>,
}

export const analyticsApi = {
  // Section 1: 任务总体情况
  getProjectOverview: () =>
    api.get('/analytics/project-overview') as Promise<ApiResponse<any>>,
  getProjectTrends: (months: string) =>
    api.get('/analytics/project-trends', { params: { months } }) as Promise<ApiResponse<any>>,
  getTmReuse: (months: string) =>
    api.get('/analytics/tm-reuse', { params: { months } }) as Promise<ApiResponse<any>>,

  // Section 2: 译者
  getTranslatorProduction: (months: string) =>
    api.get('/analytics/translator-production', { params: { months } }) as Promise<ApiResponse<any>>,
  getTranslatorEfficiency: (months: string) =>
    api.get('/analytics/translator-efficiency', { params: { months } }) as Promise<ApiResponse<any>>,

  // Section 3: 客户
  getClientOverview: (months: string) =>
    api.get('/analytics/client-overview', { params: { months } }) as Promise<ApiResponse<any>>,
  getClientFeedback: (months: string) =>
    api.get('/analytics/client-feedback', { params: { months } }) as Promise<ApiResponse<any>>,

  // Section 4: 机翻修改率
  getMtDiff: (months: string) =>
    api.get('/analytics/mt-diff', { params: { months } }) as Promise<ApiResponse<any>>,
  // 查询需要重算的项目数（前端据此决定是否显示"重新计算"按钮）
  getMtDiffPending: () =>
    api.get('/analytics/mt-diff-pending') as Promise<ApiResponse<{ pendingCount: number }>>,
  // 重新计算：对已完成但 mt_diff_result 为空的项目重算
  recomputeMtDiff: () =>
    api.post('/analytics/recompute-mt-diff') as Promise<ApiResponse<{ triggered: number; projectIds: string[]; message?: string }>>,

  // 保留：反馈统计
  getFeedbackOverview: () =>
    api.get('/analytics/feedback-overview') as Promise<ApiResponse<any>>,
  getFeedbackPendingList: () =>
    api.get('/analytics/feedback-pending-list') as Promise<ApiResponse<any>>,
}

export const archiveApi = {
  getProjects: () =>
    api.get('/archive/projects') as Promise<ApiResponse<any[]>>,

  getTasks: () =>
    api.get('/archive/tasks') as Promise<ApiResponse<any[]>>,

  archiveProject: (projectId: string, uploadToNutstore?: boolean) =>
    api.post(`/archive/projects/${projectId}`, { uploadToNutstore }) as Promise<ApiResponse<{ 
      taskId: string
      nutstorePath?: string
      error?: string
    }>>,

  batchArchive: (projectIds: string[], uploadToNutstore?: boolean) =>
    api.post('/archive/batch', { projectIds, uploadToNutstore }, {
      timeout: 120000,
    }) as Promise<ApiResponse<{
      archivedCount: number
      taskIds: string[]
    }>>,

  restoreProject: (projectId: string) =>
    api.post(`/archive/projects/${projectId}/restore`) as Promise<ApiResponse<void>>,

  retryTask: (taskId: string) =>
    api.post(`/archive/tasks/${taskId}/retry`) as Promise<ApiResponse<{ nutstorePath?: string }>>,

  batchRetryTasks: (taskIds: string[]) =>
    api.post('/archive/tasks/batch/retry', { taskIds }) as Promise<ApiResponse<{ retriedCount: number }>>,

  deleteTask: (taskId: string) =>
    api.delete(`/archive/tasks/${taskId}`) as Promise<ApiResponse<void>>,

  batchDeleteTasks: (taskIds: string[]) =>
    api.delete('/archive/tasks/batch', { data: { taskIds } }) as Promise<ApiResponse<{ deletedCount: number }>>,
}

export const llmConfigApi = {
  getProviders: () =>
    api.get('/llm-config/providers') as Promise<ApiResponse<any[]>>,

  createProvider: (data: any) =>
    api.post('/llm-config/providers', data) as Promise<ApiResponse<any>>,

  deleteProvider: (id: string) =>
    api.delete(`/llm-config/providers/${id}`) as Promise<ApiResponse<void>>,

  getScenes: () =>
    api.get('/llm-config/scenes') as Promise<ApiResponse<any[]>>,

  getScene: (scene: string) =>
    api.get(`/llm-config/scene/${scene}`) as Promise<ApiResponse<any>>,

  updateScene: (scene: string, data: any) =>
    api.put(`/llm-config/scene/${scene}`, data) as Promise<ApiResponse<void>>,

  refreshProviderModels: (id: string) =>
    api.post(`/llm-config/providers/${id}/refresh-models`) as Promise<ApiResponse<{ models: string[]; count: number }>>,
}

export const collaborationApi = {
  getProjectTranslators: (projectId: string) =>
    api.get(`/collaboration/projects/${projectId}/translators`) as Promise<ApiResponse<{
      translators: Array<{
        id: string
        translatorId: string
        translatorName: string
        translatorEmail: string
        startSequence: number | null
        endSequence: number | null
        assignedBy: string | undefined
        assignedAt: Date
        segmentCount: number
      }>
      segments: Array<{
        id: string
        sequenceNumber: number
        sourceText: string
        assignedTranslatorId: string | null
        // 计费字数（短剧按台词口径剥离后计，与详情页 sourceWordCount 一致），由后端统一计算
        wordCount: number
      }>
      totalSegments: number
    }>>,

  addTranslator: (projectId: string, data: { translatorId: string; startSequence: number; endSequence: number }) =>
    api.post(`/collaboration/projects/${projectId}/translators`, data) as Promise<ApiResponse<{
      id: string
      translatorId: string
      startSequence: number
      endSequence: number
    }>>,

  updateTranslatorRange: (projectId: string, translatorId: string, data: { startSequence: number; endSequence: number }) =>
    api.put(`/collaboration/projects/${projectId}/translators/${translatorId}`, data) as Promise<ApiResponse<void>>,

  removeTranslator: (projectId: string, translatorId: string) =>
    api.delete(`/collaboration/projects/${projectId}/translators/${translatorId}`) as Promise<ApiResponse<void>>,

  getMySegments: (projectId: string) =>
    api.get(`/collaboration/projects/${projectId}/my-segments`) as Promise<ApiResponse<{
      assignment: { startSequence: number | null; endSequence: number | null }
      mySegments: Array<{
        id: string
        sequenceNumber: number
        sourceText: string
        translation: string | null
        isLocked: boolean
        isConfirmed: boolean
        canEdit: boolean
      }>
      allSegments: Array<{
        id: string
        sequenceNumber: number
        sourceText: string
        canEdit: boolean
      }>
      totalSegments: number
      mySegmentCount: number
    }>>,

  canEditSegment: (segmentId: string) =>
    api.get(`/collaboration/segments/${segmentId}/can-edit`) as Promise<ApiResponse<{ canEdit: boolean }>>,

  getTranslatorStats: (projectId: string) =>
    api.get(`/collaboration/projects/${projectId}/translator-stats`) as Promise<ApiResponse<{
      translators: Array<{
        translatorId: string
        translatorName: string
        translatorEmail: string
        startSequence: number | null
        endSequence: number | null
        segmentCount: number
        wordCount: number
      }>
      unassigned: { segmentCount: number; wordCount: number }
      totalSegments: number
      totalWordCount: number
    }>>,

  /** 批量分配译者到多个项目（全段落范围），已分配过的项目自动跳过。 */
  batchAssign: (data: { projectIds: string[]; translatorId: string }) =>
    api.post('/collaboration/batch-assign', data) as Promise<ApiResponse<{
      assignedCount: number
      skippedCount: number
      results: Array<{ projectId: string; success: boolean; skipped?: boolean; error?: string }>
    }>>,
}

export { api }
export default api
